import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, Checkbox, Divider, Spin } from "antd";
import toast from "react-hot-toast";
import UserAvatar from "../../components/global/avtar/UserAvatar";
import LoadableButton from "../../components/buttons/LoadableButton";
import { getAllPermissions, updatePermissions } from "../../api/request/permission";

const SubadminDetails = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const { data } = await getAllPermissions({ userId: id });
      if (data?.success) {
        setData(data?.data);
        setPermissions(data?.data?.permissions || []);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load permissions.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchData();
  }, [id]);

  const handleActionChange = (moduleName, actionKey, checked, isSub = false, subName = null) => {
    setPermissions((prev) =>
      prev.map((mod) => {
        if (mod.key !== moduleName) return mod;

        if (isSub && mod.subModules) {
          return {
            ...mod,
            subModules: mod.subModules.map((sub) =>
              sub.key === subName
                ? {
                    ...sub,
                    actions: sub.actions.map((a) =>
                      a.key === actionKey
                        ? { ...a, allowed: checked }
                        : actionKey === "view" && !checked
                        ? { ...a, allowed: false }
                        : a
                    ),
                  }
                : sub
            ),
          };
        }

        return {
          ...mod,
          actions: mod.actions.map((a) =>
            a.key === actionKey
              ? { ...a, allowed: checked }
              : actionKey === "view" && !checked
              ? { ...a, allowed: false }
              : a
          ),
        };
      })
    );
  };

  const handleModuleToggle = (moduleName, checked, isSub = false, subName = null) => {
    setPermissions((prev) =>
      prev.map((mod) => {
        if (mod.key !== moduleName) return mod;

        if (isSub && mod.subModules) {
          return {
            ...mod,
            subModules: mod.subModules.map((sub) =>
              sub.key === subName
                ? {
                    ...sub,
                    actions: sub.actions.map((a) =>
                      a.key === "view"
                        ? { ...a, allowed: checked }
                        : { ...a, allowed: checked ? a.allowed : false }
                    ),
                  }
                : sub
            ),
          };
        }

        return {
          ...mod,
          actions: mod.actions.map((a) =>
            a.key === "view"
              ? { ...a, allowed: checked }
              : { ...a, allowed: checked ? a.allowed : false }
          ),
          subModules: mod.subModules
            ? mod.subModules.map((sub) => ({
                ...sub,
                actions: sub.actions.map((a) => ({
                  ...a,
                  allowed: checked ? a.allowed : false,
                })),
              }))
            : mod.subModules,
        };
      })
    );
  };

  const handleSave = async () => {
    setIsSubmitLoading(true);
    try {
      const payload = { userId: id, permissions };
      const { data } = await updatePermissions(payload);
      if (data?.success) toast.success(data?.message);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save permissions.");
    } finally {
      setIsSubmitLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-4 border border-gray-200 rounded-lg space-y-5">
      <div className="pb-5 border-b border-gray-200">
        <UserAvatar
          data={{
            name: data?.name,
            email: data?.email,
            profileImage: data?.profileImageUrl,
          }}
        />
      </div>

      <Card
        title="Module Permissions"
        extra={
          <LoadableButton
            className="flex items-center bg-primary text-sm text-white font-semibold px-6 py-2 rounded-lg themeHover duration-500"
            lable="Save Changes"
            isLoading={isSubmitLoading}
            loadingLable="Saving..."
            onClick={handleSave}
          />
        }
      >
        {permissions.map((module) => {
          const hasSubModules = module?.subModules?.length > 0;

          return (
            <div key={module.key} className="px-4">
              <Divider orientation="left" orientationMargin={0}>
                {module?.actions?.length ? (
                  <Checkbox
                    checked={module.actions.find((a) => a.key === "view")?.allowed}
                    onChange={(e) => handleModuleToggle(module.key, e.target.checked)}
                  >
                    {module.displayName}
                  </Checkbox>
                ) : (
                  <span className="text-sm font-medium">{module.displayName}</span>
                )}
              </Divider>

              {module?.actions?.length ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 ml-6 mb-2">
                  {module.actions.map((action) => (
                    <Checkbox
                      key={action.key}
                      checked={action.allowed}
                      disabled={!module.actions.find((a) => a.key === "view")?.allowed}
                      onChange={(e) =>
                        handleActionChange(module.key, action.key, e.target.checked)
                      }
                      className="!flex !items-center"
                    >
                      {action.displayName}
                    </Checkbox>
                  ))}
                </div>
              ) : null}

              {hasSubModules
                ? module.subModules.map((sub) => (
                    <div key={sub.key} className="border-l-2 border-gray-200 px-5 mx-5 mb-3">
                      <Divider orientation="left" orientationMargin={0}>
                        <Checkbox
                          checked={sub.actions.find((a) => a.key === "view")?.allowed}
                          onChange={(e) =>
                            handleModuleToggle(module.key, e.target.checked, true, sub.key)
                          }
                        >
                          {sub.displayName}
                        </Checkbox>
                      </Divider>

                      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 ml-6 mb-2">
                        {sub.actions.map((action) => (
                          <Checkbox
                            key={action.key}
                            checked={action.allowed}
                            disabled={!sub.actions.find((a) => a.key === "view")?.allowed}
                            onChange={(e) =>
                              handleActionChange(module.key, action.key, e.target.checked, true, sub.key)
                            }
                            className="!flex !items-center"
                          >
                            {action.displayName}
                          </Checkbox>
                        ))}
                      </div>
                    </div>
                  ))
                : null}
            </div>
          );
        })}
      </Card>
    </div>
  );
};

export default SubadminDetails;
